'use client';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import type { MediaType } from '@repo/ui/src/types/image';
import type { LinkObj } from '@repo/ui/src/types/link';
import type { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import YoutubePlay from '@repo/ui/src/components/Icons/YoutubePlayIcon';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { YoutubeIconContainer, StoryItemContent, StoryDescription } from './styles';
import { memo } from 'react';

interface StoriesGridItemContentProps {
  title?: string;
  description?: string;
  image?: MediaType;
  actionText?: string;
  linkObj: LinkObj | null;
  youtubeLink?: YoutubeLinkProps;
}

const StoriesGridItemContent = ({
  title,
  description,
  image,
  actionText,
  linkObj,
  youtubeLink,
}: StoriesGridItemContentProps) => {
  const imgUrl = buildImageSrc(image, 370, 16, 9);
  const smAndDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <>
      <Box sx={{ minHeight: 120, position: 'relative' }}>
        <LazyLoadImage imgComponent={<img src={imgUrl} alt={title} width="100%" />} />
        {youtubeLink && (
          <YoutubeIconContainer>
            <YoutubePlay color="#fff" height={smAndDown ? '100px' : '75px'} shadow />
          </YoutubeIconContainer>
        )}
      </Box>
      <StoryItemContent
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        p={{ xs: 2, sm: 3 }}
      >
        {title && (
          <Typography
            component="h4"
            variant="h6"
            fontWeight="bold"
            align="left"
            mb={2}
            sx={{ fontSize: { xs: '1.375rem' }, lineHeight: { xs: '1.5rem' } }}
          >
            {title}
          </Typography>
        )}
        {description && (
          <StoryDescription variant="body2" mb={1}>
            {description}
          </StoryDescription>
        )}
        {(linkObj || youtubeLink) && (
          <Typography
            variant="body2"
            sx={{
              textDecoration: 'underline',
              '&:hover': { color: (theme: Theme) => theme.palette.primary.dark },
            }}
          >
            {actionText}
          </Typography>
        )}
      </StoryItemContent>
    </>
  );
};

export default memo(StoriesGridItemContent);
